/**
 * Phase H4 — Agent Hotel Bookings tab.
 *
 * Read-mostly view of an agent's hotel bookings. Mirrors the structural
 * approach of `AgentBookings.jsx` (flights) but trimmed: hotels only need
 * list / filter / detail / cancel.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search, Filter, Calendar, Building2, Eye, Loader2, XCircle,
  CheckCircle, AlertTriangle, Users, Clock, RefreshCw, Receipt, Download,
  Zap, LifeBuoy,
} from 'lucide-react';
import AgentLayout from './AgentLayout';
import PaymentAndRefundCard from '../../components/PaymentAndRefundCard';
import RaiseTicketModal from '../../components/RaiseTicketModal';
import { useAgentAuth } from '../../context/AgentAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLES = {
  CONFIRMED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  PAYMENT_PENDING: 'bg-amber-100 text-amber-800 border-amber-200',
  HOLD_PENDING: 'bg-blue-100 text-blue-800 border-blue-200',
  ON_HOLD: 'bg-blue-100 text-blue-800 border-blue-200',
  CANCELLED: 'bg-gray-200 text-gray-700 border-gray-300',
  FAILED: 'bg-rose-100 text-rose-800 border-rose-200',
};

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const AgentHotelBookings = () => {
  const navigate = useNavigate();
  const { agent } = useAgentAuth() || {};
  const [bookings, setBookings] = useState([]);
  const [supportBooking, setSupportBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [detailRow, setDetailRow] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [refreshing, setRefreshing] = useState(null);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/agent/hotel-bookings`, {
        withCredentials: true,
        params: { page, limit: 20, ...(statusFilter && { status: statusFilter }), ...(search && { search }) },
      });
      setBookings(r.data.bookings || []);
      setTotalPages(r.data.pages || 1);
    } catch (e) {
      console.error('Agent hotel bookings fetch failed:', e);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const r = await axios.get(`${API}/agent/hotel-bookings/stats/summary`, { withCredentials: true });
      setStats(r.data.stats || null);
    } catch (e) {
      console.error('Stats fetch failed', e);
    }
  };

  useEffect(() => { fetchBookings(); }, [page, statusFilter]); // eslint-disable-line
  useEffect(() => { fetchStats(); }, []);                       // eslint-disable-line

  const onSearch = (e) => { e.preventDefault(); setPage(1); fetchBookings(); };

  const onCancel = async (row) => {
    if (!window.confirm(`Cancel booking ${row.booking_id}? This cannot be undone.`)) return;
    setCancelling(row.booking_id);
    try {
      await axios.post(`${API}/agent/hotel-bookings/${row.booking_id}/cancel`, null, { withCredentials: true });
      await fetchBookings();
      await fetchStats();
      setDetailRow(null);
    } catch (e) {
      alert(e.response?.data?.detail?.message || 'Cancellation failed. Please contact support.');
    } finally {
      setCancelling(null);
    }
  };

  const onDownloadInvoice = async (row) => {
    try {
      const r = await axios.get(
        `${API}/agent/hotel-bookings/${row.booking_id}/invoice/download`,
        { withCredentials: true, responseType: 'blob' },
      );
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${row.gst_invoice_number || row.booking_id}.pdf`.replace(/\//g, '_');
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e.response?.data?.detail?.message || e.response?.data?.detail || 'Invoice not available yet. Please retry in a minute.';
      alert(msg);
    }
  };

  const onRefreshSupplier = async (row) => {
    setRefreshing(row.booking_id);
    try {
      const r = await axios.post(
        `${API}/agent/hotel-bookings/${row.booking_id}/refresh`, null,
        { withCredentials: true },
      );
      const d = r.data || {};
      // Reflect updated row immediately in the modal (status / voucher / supplier_status_raw)
      const fresh = await axios.get(`${API}/agent/hotel-bookings/${row.booking_id}`, { withCredentials: true });
      setDetailRow(fresh.data?.booking || row);
      await fetchBookings();
      if (d.changed) {
        alert(`Booking refreshed — status: ${d.old_status} → ${d.new_status}`);
      } else {
        alert('Booking already in sync with the airline.');
      }
    } catch (e) {
      alert(e.response?.data?.detail?.message || 'Refresh failed. Please retry.');
    } finally {
      setRefreshing(null);
    }
  };

  return (
    <AgentLayout>
      <div className="p-4 sm:p-6 space-y-5" data-testid="agent-hotel-bookings-page">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#1B375B]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Hotel Bookings
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">View, manage, and cancel your hotel reservations.</p>
          </div>
          <button onClick={() => { fetchBookings(); fetchStats(); }} data-testid="agent-hotel-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-50">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="agent-hotel-stats">
            {[
              { label: 'Total', value: stats.total, icon: Building2, color: 'text-[#1B375B]' },
              { label: 'Confirmed', value: stats.confirmed, icon: CheckCircle, color: 'text-emerald-700' },
              { label: 'Pending', value: stats.pending, icon: Clock, color: 'text-amber-700' },
              { label: 'Cancelled', value: stats.cancelled, icon: XCircle, color: 'text-gray-700' },
              { label: 'Today', value: stats.today, icon: Calendar, color: 'text-blue-700' },
              { label: 'Revenue', value: fmtINR(stats.revenue), icon: Receipt, color: 'text-[#659A9B]' },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-3" data-testid={`agent-hotel-stat-${s.label.toLowerCase()}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">{s.label}</p>
                    <Icon className={`w-3.5 h-3.5 ${s.color}`} />
                  </div>
                  <p className={`text-lg font-extrabold mt-1 ${s.color}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{s.value}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap items-center gap-3">
          <form onSubmit={onSearch} className="flex-1 min-w-[240px] flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by booking ID, hotel name, guest…"
                data-testid="agent-hotel-search"
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#659A9B]" />
            </div>
            <button type="submit" className="px-4 py-2 bg-[#1B375B] text-white rounded-lg text-xs font-bold hover:bg-[#1B375B]/90">Search</button>
          </form>
          <div className="flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-gray-400" />
            <select value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
              data-testid="agent-hotel-status-filter"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#659A9B]">
              <option value="">All Status</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="PAYMENT_PENDING">Payment Pending</option>
              <option value="HOLD_PENDING">Hold Pending</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Loader2 className="w-7 h-7 animate-spin text-[#659A9B] mx-auto" />
            <p className="text-xs text-gray-500 mt-2">Loading your bookings…</p>
          </div>
        ) : bookings.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-testid="agent-hotel-empty">
            <Building2 className="w-12 h-12 text-[#1B375B]/30 mx-auto mb-2" />
            <p className="text-sm font-bold text-[#1B375B]">No hotel bookings yet</p>
            <p className="text-xs text-gray-500 mt-1">Once you book a hotel, it will appear here.</p>
            <button onClick={() => navigate('/')} className="mt-3 px-4 py-2 bg-[#1B375B] text-white rounded-lg text-xs font-bold">
              Search Hotels
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="agent-hotel-bookings-list">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Booking', 'Hotel', 'Guest', 'Dates', 'Total', 'Status', ''].map((h) => (
                      <th key={h} className="px-4 py-2.5 text-left text-[10px] uppercase tracking-wider font-bold text-gray-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.booking_id} className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors" data-testid={`agent-hotel-row-${b.booking_id}`}>
                      <td className="px-4 py-3">
                        <p className="font-extrabold text-[#1B375B] text-xs">{b.booking_id}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{fmtDateTime(b.createdAt)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-bold text-gray-800 text-xs line-clamp-1">{b.hotel_meta?.hotelName || '—'}</p>
                        <p className="text-[10px] text-gray-400 line-clamp-1">{b.hotel_meta?.city || ''}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs font-semibold text-gray-700">{b.lead_guest || '—'}</p>
                        <p className="text-[10px] text-gray-400">{(b.rooms || []).reduce((s, r) => s + (r.travellerInfo?.length || 0), 0)} guest(s)</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-[11px] text-gray-700">{fmtDate(b.hotel_meta?.checkin)}</p>
                        <p className="text-[10px] text-gray-400">→ {fmtDate(b.hotel_meta?.checkout)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-extrabold text-[#1B375B] text-sm">{fmtINR(b.total_amount)}</p>
                        <p className="text-[10px] text-gray-400 capitalize">{b.payment_mode}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold border ${STATUS_STYLES[b.status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                          {b.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setDetailRow(b)} data-testid={`agent-hotel-view-${b.booking_id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#1B375B] text-white rounded text-[10px] font-bold hover:bg-[#1B375B]/90">
                          <Eye className="w-3 h-3" /> View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-xs">
                <p className="text-gray-500">Page <span className="font-bold text-[#1B375B]">{page}</span> of {totalPages}</p>
                <div className="flex items-center gap-2">
                  <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                    className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg font-bold disabled:opacity-40 hover:bg-gray-50">Prev</button>
                  <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg font-bold disabled:opacity-40 hover:bg-gray-50">Next</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Detail modal */}
        {detailRow && (
          <BookingDetailModal
            booking={detailRow}
            onClose={() => setDetailRow(null)}
            onCancel={onCancel}
            cancelling={cancelling}
            onDownloadInvoice={onDownloadInvoice}
            onRefreshSupplier={onRefreshSupplier}
            refreshing={refreshing}
            onNeedHelp={() => {
              setSupportBooking({ booking_id: detailRow.booking_id, id: detailRow.booking_id });
              setDetailRow(null);
            }}
          />
        )}
      </div>
      <RaiseTicketModal
        open={!!supportBooking}
        onClose={() => setSupportBooking(null)}
        booking={supportBooking || {}}
        defaultEmail={agent?.email || ''}
        defaultPhone={agent?.phone || ''}
        portal="agent"
        defaultCategory="general_inquiry"
        onCreated={() => {
          setSupportBooking(null);
          navigate('/agent/support-tickets');
        }}
      />
    </AgentLayout>
  );
};

const BookingDetailModal = ({
  booking, onClose, onCancel, cancelling, onDownloadInvoice, onRefreshSupplier, refreshing, onNeedHelp,
}) => {
  const canCancel = !['CANCELLED', 'FAILED'].includes(booking.status);
  const hasInvoice = !!booking.gst_invoice_id;
  const ris = booking.hotel_meta?.rooms_in_option || [];
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="agent-hotel-detail-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] px-6 py-4 flex items-center justify-between sticky top-0">
          <div>
            <h2 className="text-white font-extrabold" style={{ fontFamily: 'Outfit, sans-serif' }}>{booking.hotel_meta?.hotelName || 'Hotel Booking'}</h2>
            <p className="text-[11px] text-white/75">{booking.booking_id} · {fmtDateTime(booking.createdAt)}</p>
          </div>
          <button onClick={onClose} data-testid="agent-hotel-detail-close" className="text-white/80 hover:text-white"><XCircle className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Status + key facts */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            {[
              { label: 'Status', value: booking.status, className: STATUS_STYLES[booking.status] || '' },
              { label: 'Supplier ID', value: booking.supplier_booking_id || '—' },
              { label: 'Check-in', value: fmtDate(booking.hotel_meta?.checkin) },
              { label: 'Check-out', value: fmtDate(booking.hotel_meta?.checkout) },
            ].map((f, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-2">
                <p className="text-[9px] uppercase tracking-wider font-bold text-gray-500">{f.label}</p>
                {f.className
                  ? <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${f.className}`}>{f.value}</span>
                  : <p className="text-xs font-extrabold text-[#1B375B] mt-1 break-words">{f.value}</p>}
              </div>
            ))}
          </div>

          {/* Phase H9 — Supplier sync metadata */}
          {(booking.voucher_ref || booking.supplier_status_raw || booking.lastSyncedAt) && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]" data-testid="agent-hotel-supplier-sync">
              {booking.voucher_ref && (
                <span><span className="font-bold text-gray-500">Voucher:</span> <span className="font-extrabold text-[#1B375B]">{booking.voucher_ref}</span></span>
              )}
              {booking.supplier_status_raw && (
                <span><span className="font-bold text-gray-500">Supplier status:</span> <span className="font-extrabold text-[#1B375B]">{booking.supplier_status_raw}</span></span>
              )}
              {booking.lastSyncedAt && (
                <span className="text-gray-500">Last synced: {fmtDateTime(booking.lastSyncedAt)}</span>
              )}
            </div>
          )}

          {/* Rooms */}
          {ris.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold text-[#659A9B] mb-2">{ris.length} room{ris.length !== 1 ? 's' : ''} booked</p>
              <div className="space-y-1.5">
                {ris.map((ri, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-xs text-gray-700 bg-gray-50 p-2 rounded-lg" data-testid={`agent-hotel-detail-room-${idx}`}>
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-extrabold bg-[#1B375B] text-white shrink-0 mt-0.5">{idx + 1}</span>
                    <div>
                      <p className="font-extrabold text-[#1B375B]">{ri.rt || ri.rc || `Room ${idx + 1}`}</p>
                      <p className="text-[10px] text-gray-500">
                        {(ri.bt || ri.bedType) && <>{ri.bt || ri.bedType} · </>}
                        {ri.adt || 0} adult{(ri.adt || 0) !== 1 ? 's' : ''}{ri.chd ? `, ${ri.chd} child${ri.chd !== 1 ? 'ren' : ''}` : ''}
                        {ri.mb && <span className="text-amber-700"> · {Array.isArray(ri.mb) ? ri.mb.join(', ') : ri.mb}</span>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Guests */}
          {(booking.rooms || []).length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold text-[#659A9B] mb-2">Guests</p>
              <div className="space-y-1">
                {booking.rooms.map((r, ri) =>
                  (r.travellerInfo || []).map((t, ti) => (
                    <div key={`${ri}-${ti}`} className="text-xs text-gray-700 flex items-center gap-2">
                      <Users className="w-3 h-3 text-gray-400" />
                      <span className="font-semibold">{t.ti} {t.fN} {t.lN}</span>
                      <span className="text-gray-400">· Room {ri + 1} · {t.pt}</span>
                    </div>
                  )),
                )}
              </div>
            </div>
          )}

          {/* Money */}
          <div className="bg-gradient-to-r from-[#1B375B]/5 to-[#659A9B]/5 p-3 rounded-xl">
            <div className="flex justify-between items-center">
              <span className="text-sm font-bold text-[#1B375B]">Total Paid</span>
              <span className="text-xl font-extrabold text-[#1B375B]" style={{ fontFamily: 'Outfit, sans-serif' }}>{fmtINR(booking.total_amount)}</span>
            </div>
            <p className="text-[10px] text-gray-500 mt-1">Payment: {booking.payment_mode}{booking.is_hold ? ' · Hold' : ''}</p>
            {hasInvoice && (
              <p className="text-[10px] text-emerald-700 mt-1 flex items-center gap-1" data-testid="agent-hotel-invoice-info">
                <Receipt className="w-3 h-3" /> Tax Invoice: <span className="font-bold">{booking.gst_invoice_number}</span>
              </p>
            )}
          </div>

          {/* Phase C1 — Payment & Refund (auto-hides cancellation block until cancelled) */}
          <PaymentAndRefundCard booking={booking} testIdPrefix="paref-agent-hotel" />

          {/* Actions */}
          <div className="border-t border-gray-100 pt-3 flex flex-wrap gap-2 justify-end">
            <button
              onClick={() => onRefreshSupplier(booking)}
              disabled={refreshing === booking.booking_id}
              data-testid="agent-hotel-refresh-supplier-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-[#1B375B]/30 text-[#1B375B] rounded-lg text-xs font-bold hover:bg-[#1B375B]/5 disabled:opacity-60"
            >
              {refreshing === booking.booking_id
                ? <><Loader2 className="w-3 h-3 animate-spin" /> Syncing…</>
                : <><Zap className="w-3 h-3" /> Refresh from supplier</>}
            </button>
            {hasInvoice && (
              <button
                onClick={() => onDownloadInvoice(booking)}
                data-testid="agent-hotel-invoice-download"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#1B375B] text-white rounded-lg text-xs font-bold hover:bg-[#1B375B]/90"
              >
                <Download className="w-3 h-3" /> Download Invoice
              </button>
            )}
            <button
              type="button"
              onClick={onNeedHelp}
              data-testid="agent-hotel-need-help-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 border border-teal-300 text-teal-800 rounded-lg text-xs font-bold hover:bg-teal-50"
            >
              <LifeBuoy className="w-3 h-3" /> Need help?
            </button>
            {canCancel && (
              <button
                onClick={() => onCancel(booking)}
                disabled={cancelling === booking.booking_id}
                data-testid="agent-hotel-cancel-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-rose-600 text-white rounded-lg text-xs font-bold hover:bg-rose-700 disabled:opacity-60"
              >
                {cancelling === booking.booking_id
                  ? <><Loader2 className="w-3 h-3 animate-spin" /> Cancelling…</>
                  : <><XCircle className="w-3 h-3" /> Cancel Booking</>}
              </button>
            )}
          </div>
          {booking.status === 'CANCELLED' && (
            <div className="bg-gray-100 rounded-lg p-3 flex items-start gap-2 text-xs text-gray-600">
              <AlertTriangle className="w-3.5 h-3.5 text-gray-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-bold">Cancelled on {fmtDateTime(booking.cancelledAt)}</p>
                {booking.cancellation_reason && <p className="text-gray-500 mt-0.5">Reason: {booking.cancellation_reason}</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentHotelBookings;
